import { useMemo, useState } from "react";

import {
  useBasket,
  useBasketSettled,
} from "~/routes/resources/hooks/basket-hooks";

import { estimatedTotalCents, orderTotalCents } from "./funds";

export interface CartSpend {
  /** The minicart's Estimated Total: what the spender ribbon and funds read. */
  cents: number;
  /** The order total (shipping in, rewards netted): the TPA tracker's trigger. */
  orderCents: number;
  /** Sum of line quantities. */
  count: number;
  /**
   * Quantity per line productId, and per the master id of a variant line, so a
   * master tile shows every variant's quantity (legacy keys by SKU only).
   */
  qtyById: ReadonlyMap<string, number>;
  /**
   * False until the first basket load settles, then true for good. A failed
   * load reads as a known empty cart, as a server-side SCAPI fault already does.
   */
  known: boolean;
}

type Basket = NonNullable<ReturnType<typeof useBasket>>;

const UNKNOWN: CartSpend = {
  cents: 0,
  orderCents: 0,
  count: 0,
  qtyById: new Map(),
  known: false,
};
const FAILED: CartSpend = { ...UNKNOWN, known: true };

/**
 * Live cart spend from the site basket. While `useBasket()` is undefined (before
 * the first load, a withheld refresh, a throttled `basket: null`) the last value
 * is held, not $0. Mid-mutation values are pre-mutation until the refresh lands.
 */
export function useCartSpend(): CartSpend {
  const ctx = useBasket();
  const settled = useBasketSettled();
  const live = useMemo(() => (ctx ? summarize(ctx) : null), [ctx]);
  const [held, setHeld] = useState(UNKNOWN);
  if (live && live !== held) setHeld(live);
  return live ?? (settled && !held.known ? FAILED : held);
}

function summarize({ basket, products }: Basket): CartSpend {
  // Variant-only master lookup, as in `hec/analytics/cart-line-detail.ts` (not
  // reused: it drops unpriced lines). `useBasketLineQuantityReader` is ref-backed,
  // never re-renders and has no master rollup, so it cannot drive a badge.
  const masterOf = new Map(
    products.flatMap(p =>
      p.type?.variant && p.master?.masterId
        ? [[p.id, p.master.masterId] as const]
        : [],
    ),
  );
  const qtyById = new Map<string, number>();
  const bump = (id: string, qty: number) =>
    qtyById.set(id, (qtyById.get(id) ?? 0) + qty);
  let count = 0;
  for (const line of basket.productItems ?? []) {
    const qty = line.quantity ?? 0;
    count += qty;
    if (!line.productId) continue;
    bump(line.productId, qty);
    const masterId = masterOf.get(line.productId);
    if (masterId) bump(masterId, qty);
  }
  return {
    cents: estimatedTotalCents(basket),
    orderCents: orderTotalCents(basket),
    count,
    qtyById,
    known: true,
  };
}
